import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Package, ChevronRight, ChevronDown, CheckCircle, Truck, Clock, ShoppingBag, CircleDot, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const STATUS_STEPS = [
  { key: 'pending', label: 'En attente', icon: Clock },
  { key: 'confirmed', label: 'Confirmée', icon: CheckCircle },
  { key: 'processing', label: 'En préparation', icon: ShoppingBag },
  { key: 'shipped', label: 'Expédiée', icon: Truck },
  { key: 'delivered', label: 'Livrée', icon: Package },
];

const getStatusIndex = (status) => {
  if (status === 'cancelled') return -1;
  return STATUS_STEPS.findIndex(s => s.key === status);
};

const StatusTimeline = ({ status }) => {
  const currentIdx = getStatusIndex(status);

  if (status === 'cancelled') {
    return (
      <div className="flex items-center gap-2 mt-3">
        <CircleDot className="w-4 h-4 text-red-400" />
        <span className="text-red-400 text-sm font-medium">Commande annulée</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 mt-3 overflow-x-auto pb-1">
      {STATUS_STEPS.map((step, idx) => {
        const Icon = step.icon;
        const isActive = idx <= currentIdx;
        const isCurrent = idx === currentIdx;
        return (
          <React.Fragment key={step.key}>
            <div className="flex flex-col items-center min-w-[60px]" data-testid={`status-step-${step.key}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                isCurrent ? 'bg-fuchsia-600 ring-2 ring-fuchsia-400/30' :
                isActive ? 'bg-fuchsia-600/60' : 'bg-white/10'
              }`}>
                <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-gray-500'}`} />
              </div>
              <span className={`text-[10px] mt-1 text-center leading-tight ${
                isCurrent ? 'text-fuchsia-400 font-medium' : isActive ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {step.label}
              </span>
            </div>
            {idx < STATUS_STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 min-w-[20px] rounded ${
                idx < currentIdx ? 'bg-fuchsia-600/60' : 'bg-white/10'
              }`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API}/orders`, { withCredentials: true });
        setOrders(response.data.orders);
      } catch {
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="orders-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 rounded-full bg-fuchsia-500/20 flex items-center justify-center">
            <Package className="w-6 h-6 text-fuchsia-400" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white font-outfit">Vos Commandes</h1>
            <p className="text-gray-400">
              {loading ? 'Chargement...' : `${orders.length} commande${orders.length !== 1 ? 's' : ''}`}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-[#121212] rounded-xl p-6 animate-pulse border border-white/10">
                <div className="h-4 bg-white/5 rounded w-1/4 mb-4" />
                <div className="h-3 bg-white/5 rounded w-1/2 mb-2" />
                <div className="h-3 bg-white/5 rounded w-1/3" />
              </div>
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-20">
            <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg mb-2">Aucune commande pour le moment</p>
            <p className="text-gray-500 text-sm mb-6">Vos commandes apparaitront ici une fois passées</p>
            <Link to="/shop">
              <Button className="bg-fuchsia-600 hover:bg-fuchsia-500" data-testid="start-shopping-button">
                Commencer vos achats
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div
                key={order.order_id}
                className="bg-[#121212] rounded-xl border border-white/10 hover:border-white/20 transition-colors overflow-hidden"
                data-testid={`order-${order.order_id}`}
              >
                <div
                  className="p-6 cursor-pointer"
                  onClick={() => setExpandedOrder(expandedOrder === order.order_id ? null : order.order_id)}
                >
                  <div className="flex items-start justify-between mb-1">
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-white font-semibold">Commande #{order.order_id.slice(-8)}</h3>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          order.status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                          order.status === 'shipped' ? 'bg-blue-500/20 text-blue-400' :
                          order.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                          'bg-yellow-500/20 text-yellow-400'
                        }`} data-testid={`order-status-${order.order_id}`}>
                          {order.status === 'confirmed' ? 'Confirmée' :
                           order.status === 'shipped' ? 'Expédiée' :
                           order.status === 'delivered' ? 'Livrée' :
                           order.status === 'cancelled' ? 'Annulée' :
                           order.status === 'processing' ? 'En préparation' :
                           'En attente'}
                        </span>
                      </div>
                      <p className="text-gray-500 text-sm">{formatDate(order.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xl font-bold text-fuchsia-400">
                        {order.total?.toFixed(2)} €
                      </span>
                      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${
                        expandedOrder === order.order_id ? 'rotate-180' : ''
                      }`} />
                    </div>
                  </div>

                  {/* Status Timeline */}
                  <StatusTimeline status={order.status} />
                </div>

                {/* Expanded Details */}
                {expandedOrder === order.order_id && (
                  <div className="px-6 pb-6 border-t border-white/10 pt-4" data-testid={`order-details-${order.order_id}`}>
                    <p className="text-gray-400 text-sm mb-3 font-medium">Articles ({order.items?.length || 0})</p>
                    <div className="space-y-2">
                      {order.items?.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="text-fuchsia-400 font-medium text-sm w-6">{item.quantity}×</span>
                            <span className="text-white text-sm">{item.name}</span>
                          </div>
                          <span className="text-gray-300 text-sm">{(item.price * item.quantity).toFixed(2)} €</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between mt-4 pt-3 border-t border-white/10">
                      <span className="text-gray-400 text-sm">
                        {order.items?.reduce((sum, item) => sum + item.quantity, 0)} articles au total
                      </span>
                      <span className="text-white font-bold">{order.total?.toFixed(2)} €</span>
                    </div>
                    
                    {/* Shipping Address */}
                    {order.shipping_address && (
                      <div className="mt-4 pt-3 border-t border-white/10" data-testid={`order-address-${order.order_id}`}>
                        <div className="flex items-center gap-2 mb-2">
                          <MapPin className="w-4 h-4 text-fuchsia-400" />
                          <p className="text-gray-400 text-sm font-medium">Adresse de livraison</p>
                        </div>
                        <div className="p-3 bg-white/5 rounded-lg text-sm text-gray-300 space-y-0.5">
                          <p className="text-white font-medium">{order.shipping_address.first_name} {order.shipping_address.last_name}</p>
                          <p>{order.shipping_address.address}</p>
                          {order.shipping_address.address2 && <p>{order.shipping_address.address2}</p>}
                          <p>{order.shipping_address.postal_code} {order.shipping_address.city}</p>
                          <p>{order.shipping_address.country}</p>
                          {order.shipping_address.phone && <p className="text-gray-400">Tel: {order.shipping_address.phone}</p>}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
